/**
 * useNotifications — React Query hooks for the notifications module.
 *
 * Polling strategy:
 *   - Unread count polls every 30 seconds (cheap, lightweight).
 *   - Full list refetches when the dropdown opens (passed `enabled` flag).
 *   - On any mutation we invalidate both queries so the bell + list stay in sync.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  type ListParams,
  type Notification,
  type NotificationListResponse,
} from '@/api/notifications';

const KEY_LIST = ['notifications', 'list'] as const;
const KEY_UNREAD = ['notifications', 'unread-count'] as const;

const POLL_INTERVAL_MS = 30_000; // 30 seconds

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: KEY_UNREAD,
    queryFn: notificationsApi.unreadCount,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    enabled,
    // The badge should always show fresh-ish data, even if the user was idle
    staleTime: 10_000,
  });
}

export function useNotificationsList(
  params: ListParams = {},
  enabled = true,
) {
  return useQuery<NotificationListResponse>({
    queryKey: [...KEY_LIST, params],
    queryFn: () => notificationsApi.list(params),
    enabled,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_UNREAD });
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_UNREAD });
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_UNREAD });
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

export function useClearRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.clearRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

// Re-export types for convenience
export type { Notification, NotificationListResponse } from '@/api/notifications';
